/* eslint-disable react/jsx-fragments */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { injectIntl } from 'react-intl';
import { decodeText } from 'app/utils/strings';
import PropTypes from 'prop-types';
import { Modal } from 'sa-ui';

import messages from '../onlineSimulatedMessages';
import './ComposingCard.scss';

const ComposingCard = (props) => {
  const {
    intl,
    composingTextSaved,
    essayDetails,
    save,
    saving,
    saved: isSaved,
    errorEssaySaveMessage,
    modalFinished,
    essayPreviewText,
  } = props;

  const [showWritingProposal, setShowWritingProposal] = useState(false);
  const [limit] = useState(essayDetails && essayDetails.maxChars ? essayDetails.maxChars : 0);
  const [count, setCount] = useState(limit);
  const [text, setText] = useState(composingTextSaved);
  const [activeTab, setActiveTab] = useState(0);
  const [saved, setSaved] = useState(false);
  const [beforeFinishErrorStatus, setBeforeFinishErrorStatus] = useState('');

  // Return early if essayDetails is not available
  if (!essayDetails) {
    return null;
  }

  const prevText = useRef('');

  const handleTabClick = (index) => {
    setActiveTab(index);
  };

  function parseMedia(text) {
    return text ? text.replace(/\/cp\/cpdata\/uploads/g, process.env.REACT_APP_FILES_API) : '';
  }

  function handleShowWritingProposal() {
    setShowWritingProposal(true);
  }

  function countCaracters(e) {
    const text = e.target.value;
    setText(text);
    setCount(limit - text.length);

    if (text.length >= limit) {
      setBeforeFinishErrorStatus('max');
    }
    if (text.length < essayDetails.minChars) {
      setBeforeFinishErrorStatus('min');
    }
  }

  const handlePaste = (event) => {
    event.preventDefault(); // Prevent pasting
    // alert('Pasting is disabled in this textarea.');
  };

  useEffect(() => {
    if (composingTextSaved) {
      if (prevText.current !== composingTextSaved) {
        prevText.current = composingTextSaved;
      }

      setText(composingTextSaved);
      setCount(limit - composingTextSaved.length);
    }
  }, [composingTextSaved]);

  useEffect(() => {
    if (text.length >= limit) {
      setBeforeFinishErrorStatus('max');
    }
    if (text.length < essayDetails.minChars) {
      setBeforeFinishErrorStatus('min');
    }

    if (prevText.current !== text && text.length > 0 && text.length <= limit) {
      essayPreviewText(text);
    }
    const interval = setInterval(() => {
      if (prevText.current !== text && text.length > 0 && text.length <= limit) {
        prevText.current = text;
        save(text);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [text]);

  useEffect(() => {
    if (modalFinished) {
      if (prevText.current !== text && text.length > 0 && text.length <= limit) {
        prevText.current = text;
        save(text);
      }
    }
  }, [modalFinished]);

  useEffect(() => {
    if (isSaved) {
      setSaved(true);

      const timeoutId = setTimeout(() => {
        setSaved(false);
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  }, [isSaved]);

  useEffect(() => {
    if (beforeFinishErrorStatus !== '') {
      props.setFinishErrorStatus(beforeFinishErrorStatus);
    }
  }, [beforeFinishErrorStatus]);

  const percentComplete = ((limit - count) / limit) * 100;

  const getContent = (tabIndex) => {
    switch (tabIndex) {
      case 0:
        return (
          <div>
            <h2>{essayDetails.title}</h2>
            <p dangerouslySetInnerHTML={{ __html: essayDetails.proposal || '' }} />
          </div>
        );
      default:
        if (tabIndex >= 1 && tabIndex <= essayDetails.supportTexts.length) {
          const supportTextIndex = tabIndex - 1;
          const supportText = essayDetails.supportTexts[supportTextIndex];
          return (
            <div>
              <h2 dangerouslySetInnerHTML={{ __html: supportText.content || '' }} />
              <p dangerouslySetInnerHTML={{ __html: supportText.reference || '' }} />
            </div>
          );
        } else {
          return null;
        }
    }
  };

  const tabs = [
    intl.formatMessage(messages.proposal),
    ...essayDetails.supportTexts.map((supportText, index) => supportText.title),
  ];

  return (
    <div style={{ textAlign: 'left' }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <strong style={{ backgroundColor: '#eee', padding: '10px 20px' }}>
          {intl.formatMessage(messages.composing)}
        </strong>

        <button
          type="button"
          className="navQuestionBtn"
          onClick={() => handleShowWritingProposal()}
        >
          {intl.formatMessage(messages.writingProposal)}
        </button>
      </div>
      <div className="row">
        <div className="col-lg-12">
          <div className="form-group">
            <div className="error">{errorEssaySaveMessage}</div>
            <textarea
              className="form-control mb-3"
              id="textarea"
              rows="10"
              maxLength={limit}
              value={decodeText(parseMedia(text))}
              onChange={(e) => countCaracters(e)}
              onPaste={handlePaste}
            ></textarea>

            <div className="text-right d-flex justify-content-between">
              <div className="d-flex align-items-center">
                <svg height="16" width="16">
                  <circle cx="8" cy="8" r="6" stroke="#ddd" strokeWidth="3" fill="transparent" />
                  <circle
                    cx="8"
                    cy="8"
                    r="6"
                    stroke="#EA654B"
                    strokeWidth="3"
                    fill="transparent"
                    strokeDasharray={`${percentComplete}, 100`}
                  />
                </svg>
                <span className="text-muted ml-2">
                  {count} {intl.formatMessage(messages.characters)}{' '}
                  {intl.formatMessage(messages.left)}
                </span>
              </div>

              <div className="d-flex align-items-center">
                {saving && <div className="mr-2">{intl.formatMessage(messages.saving)}...</div>}
                {saved && <div className="mr-2">{intl.formatMessage(messages.saved)}</div>}
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ marginLeft: '10px' }}
                  onClick={() => save(text)}
                  disabled={
                    saving || text.length === 0 || prevText.current === text || text.length > limit
                  }
                >
                  {intl.formatMessage(messages.save)}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        medium
        show={showWritingProposal}
        title={intl.formatMessage(messages.writingProposal)}
        onClose={() => setShowWritingProposal(false)}
        buttons={[
          {
            onClick: () => setShowWritingProposal(false),
            label: intl.formatMessage(messages.close),
          },
        ]}
      >
        <div className="mt-3">
          <div className="tabs">
            {tabs.map((tabName, index) => (
              <div
                key={index}
                className={`tab ${activeTab === index ? 'active' : ''}`}
                onClick={() => handleTabClick(index)}
              >
                {tabName}
              </div>
            ))}
          </div>
          <div className="tab-content">{getContent(activeTab)}</div>
        </div>
      </Modal>
    </div>
  );
};

ComposingCard.propTypes = {
  save: PropTypes.func.isRequired,
};

ComposingCard.defaultProps = {};

export default injectIntl(ComposingCard);
